/**
 * The data used for analytics.
 */
export interface AnalyticsData {
  [index: string]: unknown;
}

/**
 * An abstract interface for Google Analytics.
 */
export abstract class AnalyticsWrapper {
  /**
   * Sends a tracking beacon to Google's collection servers.
   *
   * @param type the type of the tracking hit, e.g. 'trackPageview'.
   * @param data the data to be tracked.
   * @param timeout the optional timeout to resolve the returned promise.
   *
   * @return a promise when the GTM push returns; if the server doesn't return,
   * the promise will resolve after the optional given timeout, in case client
   * code rely on it.
   *
   * @throws Error if type is null or empty.
   */
  abstract sendHit(
    type: string,
    data?: AnalyticsData,
    timeout?: number,
  ): Promise<void>;

  /**
   * Updates the current associated value with the given field. The field won't
   * be sent to server until the next GTM push happens.
   *
   * @throws Error if type is null or empty.
   */
  abstract setField(fieldName: string, value: unknown): void;

  /**
   * Updates the GTM data layer with new or updated key/value pairs.
   *
   * @param data the data to be tracked.
   * @param timeout the optional timeout to resolve the returned promise.
   *
   * @return a promise when the GTM push returns; if the server doesn't return,
   * the promise will resolve after the optional given timeout, in case client
   * code rely on it.
   */
  abstract push(data: AnalyticsData, timeout?: number): Promise<void>;
}
